import { useUpdateScreenChatContext } from '$tablet-app/src/app/chat/hooks';
import { ScreenHeader } from '$tablet-app/src/app/components/ScreenHeader';
import { ScreenTitle } from '$tablet-app/src/app/components/ScreenTitle';
import { DeveloperScreenInfo } from '$tablet-app/src/app/developer/components/DeveloperScreenInfo/DeveloperScreenInfo';
import { MainDrawerScreenProps } from '$tablet-app/src/app/navigation/MainDrawerNavigator';
import { Button } from '@akin-home/buttons/native';
import { colors } from '@akin-home/theme';
import { showErrorToast } from '@akin-home/toast/native';
import React, { useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { spacing } from '../../../theme';
import { ConnectToRobotButton } from '../../bluetooth/BluetoothRobotConnectScreen/ConnectToRobotButton';
import { KnownRobotsBar } from '../../components/KnownRobotsBar';
import { RobotAvatarBadge } from '../../components/RobotAvatarBadge';
import { useGetRobotInfo } from '../../info/queries';
import { useRobotStateStore } from '../../stores/useRobotStateStore';
import { RobotDevice } from '../../types';
import { getRobotTypeDisplayName } from '../../utils';
import { useRobotConnection } from '../../websockets/hooks/useRobotConnection';
import { RobotActionsButton } from '../components/RobotActionsButtton';
import { RobotSettingsSwitch } from '../components/RobotSettingsSwitch';
import DeleteButton from '../../components/DeleteButton';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

const RobotsScreen = (props: MainDrawerScreenProps<'Robots'>) => {
  const {
    navigation: { navigate },
  } = props;
  // const is_focused = useIsFocused();

  // const { data: info_video } = useGetSystemProperty(
  //   InfoVideoName.ROBOTS_SCREEN
  // );

  useUpdateScreenChatContext();

  // TODO: Update when intents for Robots Screen has been created
  // useEffect(() => {
  //   if (is_focused && !isLoading) {
  //     sendBackgroundMessage(intent_labels.open_checklists_screen);
  //   }
  // }, [is_focused, isLoading]);

  // TODO: Update with video for Robots Screen
  // if (!isLoading && !data.length && info_video?.value?.length) {
  //   return (
  //     <SectionIntroScreen
  //       screen_title="Task Lists"
  //       video_title="Watch the video and learn how to add your first checklist"
  //       source={{ drive_id: info_video.value }}
  //       action_button={
  //         <Button icon="plus" onPress={() => onPressNewChecklist()}>
  //           Add your first checklist
  //         </Button>
  //       }
  //     />
  //   );
  // }

  const [selectedRobot, setSelectedRobot] = useState<RobotDevice | undefined>(
    undefined
  );

  const { connectionStatus, sendRequest } = useRobotConnection({
    robotId: selectedRobot?.id ?? '',
  });

  const isRobotConnected =
    selectedRobot !== undefined && connectionStatus === 'connected';

  const deleteRobotDevice = useRobotStateStore(
    (state) => state.deleteRobotDevice
  );

  const { data: robotInfo } = useGetRobotInfo(selectedRobot);

  return (
    <View style={styles.screen}>
      <ScreenHeader>
        <ScreenTitle>Robots</ScreenTitle>
        <View style={[styles.knownRobotsContainer]}>
          <KnownRobotsBar onSelectRobot={(robot) => setSelectedRobot(robot)} />
        </View>
      </ScreenHeader>

      <View style={styles.bodyContainer}>
        {/* Robot Actions TODO: Move this to its own component*/}
        <View style={[styles.innerBodyContainer, styles.robotActionsContainer]}>
          {/* Teleop */}
          {isAnyValueTrue(robotInfo?.actions?.remote_control) ? (
            <RobotActionsButton
              style={styles.robotActionsButton}
              text={'Remote Control'}
              icon={'steering-wheel'}
              textColor={colors.blue[700]}
              iconColor={colors.blue[700]}
              disabled={!isRobotConnected}
              onPress={() => {
                if (selectedRobot !== undefined) {
                  navigate('RobotTeleop', { robotId: selectedRobot?.id });
                }
              }}
            />
          ) : null}

          {/* Press To Talk Button */}
          {robotInfo?.actions?.push_to_talk ? (
            <RobotActionsButton
              style={styles.robotActionsButton}
              text={'Push To Talk'}
              icon={'microphone'}
              textColor={colors.blue[700]}
              iconColor={colors.blue[700]}
              disabled={!isRobotConnected}
              onPress={async () => {
                if (isRobotConnected) {
                  const success = await sendRequest(500, 'push_to_talk_pressed')
                    .then((res) => res !== false)
                    .catch((err) => false);

                  if (!success) {
                    showErrorToast({
                      text1: 'Failed to activate push to talk',
                      text2: `${
                        toCapitalCase(robotInfo?.details?.name) ?? 'The robot'
                      } may already be listening or speaking. Please try again in a bit.`,
                    });
                  }
                }
              }}
            />
          ) : null}

          {/* Manage Identites Button */}
          {robotInfo?.actions?.manage_identities ? (
            <RobotActionsButton
              style={styles.robotActionsButton}
              text={'Manage Identities'}
              icon={'user'}
              textColor={colors.blue[700]}
              iconColor={colors.blue[700]}
              disabled={!isRobotConnected}
              onPress={async () => {
                if (selectedRobot !== undefined) {
                  navigate('RobotIdentities', { robotId: selectedRobot?.id });
                }
              }}
            />
          ) : null}

          {/* Tutorials Button */}
          {robotInfo?.actions?.tutorials ? (
            <RobotActionsButton
              style={styles.robotActionsButton}
              text={'Tutorials'}
              icon={'fa-regular fa-wand-magic-sparkles' as IconProp}
              textColor={colors.blue[700]}
              iconColor={colors.blue[700]}
              disabled={!isRobotConnected}
              onPress={async () => {
                if (selectedRobot !== undefined) {
                  navigate('RobotTutorials', { robotId: selectedRobot?.id });
                }
              }}
            />
          ) : null}
        </View>

        <View style={styles.spacer}></View>

        <View style={styles.innerBodyContainer}>
          {/* Avatar Bubble */}
          <View style={styles.avatarContainer}>
            <RobotAvatarBadge robotDevice={selectedRobot} size={230} />
          </View>

          {/* Delete + Close buttons */}
          {selectedRobot !== undefined ? (
            <View style={styles.floatingButtonsContainer}>
              {/* Delete button */}
              <DeleteButton
                size="large"
                deleteModalProps={{
                  title: 'Are you sure you want to delete this robot?',
                  subtitle:
                    'You can add it again later from the robots screen.',
                  confirmDeleteString: `Delete ${
                    selectedRobot.name ?? 'the robot'
                  }`,
                }}
                onConfirm={async () => {
                  await deleteRobotDevice(selectedRobot.id);
                  setSelectedRobot(undefined);
                }}
              />

              {/* Close button */}
              <Button
                icon="times"
                size="medium"
                iconSize={20}
                variant="secondary"
                onPress={() => {
                  setSelectedRobot(undefined);
                }}
              />
            </View>
          ) : null}

          {/* Robot Info */}
          <View style={styles.robotInfoContainer}>
            <View style={{ height: '60%' }}>
              <Text
                style={styles.robotNameText}
                adjustsFontSizeToFit
                numberOfLines={1}
              >
                {selectedRobot?.name ?? ''}
              </Text>
            </View>

            <View style={{ height: '40%' }}>
              <Text
                style={styles.robotModelText}
                adjustsFontSizeToFit
                numberOfLines={1}
              >
                {getRobotTypeDisplayName(selectedRobot?.type)
                  ? `(Model: ${getRobotTypeDisplayName(selectedRobot?.type)})`
                  : ''}
              </Text>
            </View>
          </View>

          {/* Settings Section */}
          <View style={styles.settingsContainer}>
            <RobotSettingsSwitch
              robot={selectedRobot}
              settingName={'mic_enabled'}
              style={styles.settingsSwitchContainer}
              headerProps={{ icon: 'microphone', text: 'Microphone:' }}
              disabled={!isRobotConnected}
            />

            <RobotSettingsSwitch
              robot={selectedRobot}
              settingName={'sounds_enabled'}
              style={styles.settingsSwitchContainer}
              headerProps={{ icon: 'volume', text: 'Sound:' }}
              disabled={!isRobotConnected}
            />

            <View style={styles.settingsButtonContainer}>
              <Button
                icon="gear"
                size="medium"
                iconSize={20}
                variant="secondary"
                disabled={selectedRobot === undefined}
                textStyle={
                  selectedRobot === undefined && { color: colors.grey.dim }
                }
                iconColor={
                  selectedRobot === undefined ? colors.grey.dim : undefined
                }
                onPress={() => {
                  if (selectedRobot !== undefined) {
                    navigate('RobotSettings', { robotId: selectedRobot?.id });
                  }
                }}
              >
                More Settings
              </Button>
            </View>
          </View>

          <ConnectToRobotButton robotDevice={selectedRobot} />
        </View>

        <DeveloperScreenInfo />
      </View>
    </View>
  );
};

const toCapitalCase = (word: string | undefined) => {
  if (word === undefined || word === null || word.length === 0) {
    return word;
  }

  return word.charAt(0).toUpperCase() + word.substring(1).toLowerCase();
};

const isAnyValueTrue = (obj: unknown | undefined | null) => {
  if (obj === undefined || obj === null) {
    return false;
  }

  return Object.values(obj).some((elem) => !!elem);
};

const styles = StyleSheet.create({
  screen: {
    flex: 1,
  },
  bodyContainer: {
    flex: 1,
    flexDirection: 'row',
    margin: spacing[3],
    marginTop: spacing[0],
    backgroundColor: 'white',
    elevation: 3,
    borderRadius: 10,
  },
  spacer: {
    backgroundColor: '#dddddd',
    width: 1,
    borderRadius: 10,
  },
  innerBodyContainer: {
    flex: 1,
    padding: spacing[2],
    margin: spacing[2],
    borderRadius: 10,
  },
  robotActionsContainer: {
    paddingLeft: spacing[3],
    paddingTop: spacing[3],
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    alignContent: 'flex-start',
    gap: spacing[5],
  },
  robotActionsButton: {
    width: '22%',
    aspectRatio: 1,
    // height needed to make sure flexwrap works, i dont think it actually does anything due to aspect ratio set above.
    height: 0,
  },
  knownRobotsContainer: {
    width: '60%',
    flexDirection: 'row',
  },
  avatarContainer: {
    width: '100%',
    alignItems: 'center',
  },
  floatingButtonsContainer: {
    position: 'absolute',
    right: 0,
    top: 0,
    flexDirection: 'row',
    gap: spacing[2],
    justifyContent: 'flex-end',
  },
  robotInfoContainer: {
    flexGrow: 0,
    flexShrink: 1,
    overflow: 'hidden',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    margin: spacing[2],
  },
  robotNameText: {
    fontSize: 50,
    color: 'black',
    textAlign: 'center',
    fontWeight: 'bold',
    textTransform: 'capitalize',
  },
  robotModelText: {
    fontSize: 16,
    color: 'black',
    textAlign: 'center',
    fontStyle: 'italic',
  },
  settingsContainer: {
    margin: spacing[2],
    flexDirection: 'row',
    gap: spacing[2],
  },
  settingsSwitchContainer: {
    paddingRight: spacing[3],
    borderEndWidth: 1,
  },
  settingsButtonContainer: {
    marginLeft: 'auto',
    flex: 1,
  },
});

export default RobotsScreen;
